import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { fmtRM, STAGE_LABELS, STAGES, STAGE_COLORS } from "@/lib/constants";
import {
  MessageSquare, Flame, Clock, Bot, Users, Trophy, DollarSign, TrendingUp, ArrowRight, Megaphone
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, sub, tone = "primary", testid }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
    className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
        tone === "rose" ? "bg-rose-100 text-rose-600" : tone === "amber" ? "bg-amber-100 text-amber-600" :
        tone === "sky" ? "bg-sky-100 text-sky-600" : "bg-accent text-primary"}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <p className="mt-3 text-2xl font-bold font-display">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </motion.div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/dashboard/stats").then((r) => r.data) });

  if (isLoading || !data) return <div className="p-6"><div className="h-8 w-40 bg-secondary rounded animate-pulse" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Selamat kembali 👋</h1>
        <p className="text-muted-foreground text-sm mt-1">Ringkasan aktiviti WhatsApp bisnes anda hari ini.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={MessageSquare} label="Perbualan Hari Ini" value={data.conversations_today} tone="primary" testid="stat-conversations" />
        <StatCard icon={Flame} label="Hot Leads" value={data.hot_leads} sub="Score ≥ 85" tone="rose" testid="stat-hot-leads" />
        <StatCard icon={Clock} label="Menunggu Staff" value={data.waiting_staff} tone="amber" testid="stat-waiting" />
        <StatCard icon={Bot} label="AI Handling" value={data.ai_handling} tone="sky" testid="stat-ai-handling" />
        <StatCard icon={Users} label="Jumlah Pelanggan" value={data.total_contacts} testid="stat-contacts" />
        <StatCard icon={Trophy} label="Deals Won" value={data.deals_won} sub={`${data.conversion_rate}% conversion`} tone="primary" testid="stat-won" />
        <StatCard icon={DollarSign} label="Revenue" value={fmtRM(data.revenue)} tone="primary" testid="stat-revenue" />
        <StatCard icon={Megaphone} label="Kempen Aktif" value={data.campaigns_active} tone="sky" testid="stat-campaigns" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pipeline overview */}
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display font-semibold">Sales Pipeline</h2>
            <button onClick={() => navigate("/app/pipeline")} className="text-sm text-primary font-medium flex items-center gap-1" data-testid="view-pipeline-link">
              Lihat semua <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3">
            {STAGES.map((st) => {
              const count = data.pipeline[st] || 0;
              const max = Math.max(...Object.values(data.pipeline), 1);
              return (
                <div key={st} className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-md w-28 text-center ${STAGE_COLORS[st]}`}>{STAGE_LABELS[st]}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(count / max) * 100}%` }} />
                  </div>
                  <span className="text-sm font-semibold w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team status */}
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display font-semibold mb-5">Status Pasukan</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Staff Online</span>
              <span className="font-semibold flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-green-500" />{data.staff_online} / {data.staff_total}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Perlu Perhatian</span>
              <span className="font-semibold text-amber-600">{data.waiting_staff} chat</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Revenue dari Broadcast</span>
              <span className="font-semibold text-primary">{fmtRM(data.campaign_revenue)}</span>
            </div>
            <div className="pt-3 border-t border-border">
              <div className="rounded-xl bg-accent/60 p-4 flex items-start gap-3">
                <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-accent-foreground">Conversion {data.conversion_rate}%</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Naik dari WhatsApp ke closed deals.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
